import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Coins, Bot, AlertCircle, Loader2, ShoppingCart, History, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../styles/theme';
import api from '../../services/api';

/**
 * CreditsDashboard
 * Exibe saldo de créditos (base + extras), consumo por agente, custo por mensagem,
 * gráfico de consumo diário, botão de compra de créditos extras e histórico de ciclos.
 *
 * Props:
 *   - tenantId: string (obrigatório)
 */

// ── Color palette for chart bars (one per agent) ──
const CHART_COLORS = [
  theme.colors.primary,
  theme.colors.secondary,
  theme.colors.success,
  theme.colors.warning,
  '#7c3aed', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#f97316', // orange
];

const CreditsDashboard = ({ tenantId }) => {
  const { t } = useTranslation('billing');
  const [credits, setCredits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [agents, setAgents] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buying, setBuying] = useState(false);
  const [buyResult, setBuyResult] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [showBuyConfirm, setShowBuyConfirm] = useState(false);

  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const [creditsRes, usageRes, agentsRes, historyRes, tierRes] = await Promise.all([
        api.get(`/billing/${tenantId}/credits`),
        api.get(`/billing/${tenantId}/credits/usage`),
        api.get(`/marketplace/tenants/${tenantId}/agents`).catch(() => ({ data: { agents: [] } })),
        api.get(`/billing/${tenantId}/credits/history`).catch(() => ({ data: { snapshots: [] } })),
        api.get(`/platform/tiers?tenantId=${tenantId}`).catch(() => ({ data: { tiers: [] } })),
      ]);
      setCredits(creditsRes.data);
      setUsage(usageRes.data);
      setAgents(agentsRes.data.agents || []);
      setHistory(historyRes.data.snapshots || []);
      // Resolve tier info for extra pack pricing
      const tenantRes = await api.get(`/tenants/${tenantId}`).catch(() => ({ data: {} }));
      const tenantTier = tenantRes.data?.tier;
      const tiers = tierRes.data.tiers || [];
      const found = tiers.find(ti => ti.tierId === tenantTier);
      if (found) setTierInfo(found);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBuyCredits = async () => {
    setShowBuyConfirm(false);
    setBuying(true);
    setBuyResult(null);
    try {
      const res = await api.post(`/billing/${tenantId}/buy-credits`);
      setBuyResult({ success: true, message: res.data.message });
      // Refresh data after purchase
      await fetchData();
    } catch (err) {
      setBuyResult({
        success: false,
        message: err.response?.data?.message || err.message,
      });
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Loader2 size={24} className="spin" />
        <span>{t('creditsDashboard.loading', { defaultValue: 'Carregando...' })}</span>
      </LoadingWrap>
    );
  }

  if (error) {
    return (
      <ErrorWrap>
        <AlertCircle size={20} />
        <span>{t('creditsDashboard.error', { defaultValue: 'Erro ao carregar dados de créditos.' })}</span>
      </ErrorWrap>
    );
  }

  if (!credits) return null;

  const totalAvailable = credits.totalCreditsAvailable || 0;
  const totalUsed = credits.creditsUsedThisCycle || 0;
  const baseRemaining = credits.baseCreditsRemaining || 0;
  const extraRemaining = credits.extraCreditsRemaining || 0;
  const totalRemaining = baseRemaining + extraRemaining;
  const usagePercent = totalAvailable > 0 ? Math.min((totalUsed / totalAvailable) * 100, 100) : 0;
  const maxExtraPacks = credits.maxExtraPacksPerMonth || 0;
  const packsPurchased = credits.extraPacksPurchased || 0;
  const canBuyMore = packsPurchased < maxExtraPacks;

  const barColor =
    usagePercent >= 100
      ? theme.colors.danger
      : usagePercent >= 80
        ? theme.colors.warning
        : theme.colors.success;

  // Build agent map: instanceId -> { name, icon, creditsPerMessage }
  const agentMap = {};
  for (const agent of agents) {
    const id = agent.instanceId || agent.agentId;
    agentMap[id] = {
      name: agent.instanceName || agent.name || id,
      icon: agent.icon || '🤖',
      creditsPerMessage: agent.creditsPerMessage ?? null,
    };
  }

  // Aggregate usage by agent
  const usageByAgent = usage?.usageByAgent || {};
  const agentUsageList = Object.entries(usageByAgent)
    .map(([agentId, categories]) => {
      const totalCredits = Object.values(categories).reduce((sum, v) => sum + v, 0);
      const info = agentMap[agentId] || { name: agentId, icon: '🤖', creditsPerMessage: null };
      return { agentId, totalCredits, ...info };
    })
    .sort((a, b) => b.totalCredits - a.totalCredits);

  // Agents with cost per message (all installed agents, even those with no usage yet)
  const agentCostList = agents
    .filter((a) => a.creditsPerMessage != null)
    .map((a) => ({
      id: a.instanceId || a.agentId,
      name: a.instanceName || a.name || a.agentId,
      icon: a.icon || '🤖',
      creditsPerMessage: a.creditsPerMessage,
    }));

  // ── Build daily consumption chart data ──
  const dailyChartData = buildDailyChartData(usageByAgent, agentMap);

  return (
    <Wrap>
      {/* ── Balance card ── */}
      <Card>
        <CardHeader>
          <CardIcon><Coins size={18} /></CardIcon>
          <CardTitle>{t('creditsDashboard.balance', { defaultValue: 'Saldo de Créditos' })}</CardTitle>
        </CardHeader>

        <BalanceRow>
          <BalanceValue>{totalRemaining.toLocaleString()}</BalanceValue>
          <BalanceTotal>
            / {totalAvailable.toLocaleString()} {t('creditsDashboard.credits', { defaultValue: 'créditos' })}
          </BalanceTotal>
        </BalanceRow>

        <Track>
          <Fill style={{ width: `${usagePercent}%`, background: barColor }} />
        </Track>

        <BalanceMeta>
          <MetaItem>
            {t('creditsDashboard.baseRemaining', { defaultValue: 'Base' })}: <strong>{baseRemaining.toLocaleString()}</strong>
          </MetaItem>
          <MetaItem>
            {t('creditsDashboard.extraRemaining', { defaultValue: 'Extras' })}: <strong>{extraRemaining.toLocaleString()}</strong>
          </MetaItem>
          <MetaItem>
            {t('creditsDashboard.used', { defaultValue: 'Usados' })}: <strong>{totalUsed.toLocaleString()}</strong>
          </MetaItem>
        </BalanceMeta>

        {usagePercent >= 100 && (
          <AlertBadge $color={theme.colors.danger}>
            {t('creditsDashboard.exhausted', { defaultValue: '🚨 Créditos esgotados' })}
          </AlertBadge>
        )}
        {usagePercent >= 80 && usagePercent < 100 && (
          <AlertBadge $color={theme.colors.warning}>
            {t('creditsDashboard.nearLimit', {
              percent: Math.round(usagePercent),
              defaultValue: '⚠️ {{percent}}% dos créditos utilizados',
            })}
          </AlertBadge>
        )}

        {/* ── Buy extra credits button ── */}
        <BuySection>
          <BuyButton onClick={() => setShowBuyConfirm(true)} disabled={buying || !canBuyMore}>
            <ShoppingCart size={16} />
            {buying
              ? t('creditsDashboard.buyingCredits', { defaultValue: 'Processando...' })
              : t('creditsDashboard.buyCredits', { defaultValue: 'Comprar créditos extras' })}
          </BuyButton>
          <PacksInfo>
            {t('creditsDashboard.packsUsed', {
              purchased: packsPurchased,
              max: maxExtraPacks,
              defaultValue: '{{purchased}}/{{max}} pacotes comprados neste ciclo',
            })}
          </PacksInfo>
          {buyResult && (
            <BuyResultMsg $success={buyResult.success}>
              {buyResult.message}
            </BuyResultMsg>
          )}
          {showBuyConfirm && tierInfo && (
            <BuyConfirmBox>
              <BuyConfirmTitle>{t('creditsDashboard.confirmTitle', { defaultValue: 'Confirmar compra de créditos' })}</BuyConfirmTitle>
              <BuyConfirmDetails>
                <BuyConfirmRow>
                  <span>{t('creditsDashboard.confirmCredits', { defaultValue: 'Créditos' })}</span>
                  <strong>{(tierInfo.extraPackCredits || 0).toLocaleString()}</strong>
                </BuyConfirmRow>
                <BuyConfirmRow>
                  <span>{t('creditsDashboard.confirmPrice', { defaultValue: 'Valor' })}</span>
                  <strong>
                    {(() => {
                      const prices = tierInfo.extraPackPrice || {};
                      const currency = Object.keys(prices)[0] || 'BRL';
                      const price = prices[currency] || 0;
                      return price.toLocaleString('pt-BR', { style: 'currency', currency });
                    })()}
                  </strong>
                </BuyConfirmRow>
              </BuyConfirmDetails>
              <BuyConfirmActions>
                <BuyConfirmCancel onClick={() => setShowBuyConfirm(false)}>
                  {t('creditsDashboard.confirmCancel', { defaultValue: 'Cancelar' })}
                </BuyConfirmCancel>
                <BuyConfirmBtn onClick={handleBuyCredits}>
                  {t('creditsDashboard.confirmBuy', { defaultValue: 'Confirmar compra' })}
                </BuyConfirmBtn>
              </BuyConfirmActions>
            </BuyConfirmBox>
          )}
        </BuySection>
      </Card>

      {/* ── Usage by agent ── */}
      <Card>
        <CardHeader>
          <CardIcon><Bot size={18} /></CardIcon>
          <CardTitle>{t('creditsDashboard.usageByAgent', { defaultValue: 'Consumo por Agente' })}</CardTitle>
        </CardHeader>

        {agentUsageList.length === 0 ? (
          <EmptyText>{t('creditsDashboard.noUsage', { defaultValue: 'Nenhum consumo registrado neste ciclo.' })}</EmptyText>
        ) : (
          <AgentList>
            {agentUsageList.map((agent) => (
              <AgentRow key={agent.agentId}>
                <AgentInfo>
                  <AgentIcon>{agent.icon}</AgentIcon>
                  <AgentName>{agent.name}</AgentName>
                </AgentInfo>
                <AgentCredits>
                  {agent.totalCredits.toLocaleString()} {t('creditsDashboard.credits', { defaultValue: 'créditos' })}
                </AgentCredits>
              </AgentRow>
            ))}
          </AgentList>
        )}
      </Card>

      {/* ── Cost per message ── */}
      {agentCostList.length > 0 && (
        <Card>
          <CardHeader>
            <CardIcon><Coins size={18} /></CardIcon>
            <CardTitle>{t('creditsDashboard.costPerMessage', { defaultValue: 'Custo por Mensagem' })}</CardTitle>
          </CardHeader>

          <AgentList>
            {agentCostList.map((agent) => (
              <AgentRow key={agent.id}>
                <AgentInfo>
                  <AgentIcon>{agent.icon}</AgentIcon>
                  <AgentName>{agent.name}</AgentName>
                </AgentInfo>
                <CostBadge>
                  {t('creditsDashboard.creditsPerMsg', {
                    count: agent.creditsPerMessage,
                    defaultValue: '{{count}} créditos/mensagem',
                  })}
                </CostBadge>
              </AgentRow>
            ))}
          </AgentList>
        </Card>
      )}

      {/* ── Daily consumption chart ── */}
      {dailyChartData.days.length > 0 && (
        <Card>
          <CardHeader>
            <CardIcon><BarChart3 size={18} /></CardIcon>
            <CardTitle>{t('creditsDashboard.dailyChart', { defaultValue: 'Consumo Diário por Agente' })}</CardTitle>
          </CardHeader>

          <ChartLegend>
            {dailyChartData.agentIds.map((agentId, idx) => {
              const info = agentMap[agentId] || { name: agentId, icon: '🤖' };
              return (
                <LegendItem key={agentId}>
                  <LegendDot style={{ background: CHART_COLORS[idx % CHART_COLORS.length] }} />
                  <LegendLabel>{info.name}</LegendLabel>
                </LegendItem>
              );
            })}
          </ChartLegend>

          <ChartContainer>
            {dailyChartData.days.map((day) => (
              <ChartColumn key={day.label}>
                <BarStack>
                  {dailyChartData.agentIds.map((agentId, idx) => {
                    const value = day.agents[agentId] || 0;
                    const heightPct = dailyChartData.maxTotal > 0
                      ? (value / dailyChartData.maxTotal) * 100
                      : 0;
                    return heightPct > 0 ? (
                      <Bar
                        key={agentId}
                        style={{
                          height: `${heightPct}%`,
                          background: CHART_COLORS[idx % CHART_COLORS.length],
                        }}
                        title={`${(agentMap[agentId] || { name: agentId }).name}: ${value}`}
                      />
                    ) : null;
                  })}
                </BarStack>
                <DayTotal>{day.total > 0 ? day.total : ''}</DayTotal>
                <DayLabel>{day.label}</DayLabel>
              </ChartColumn>
            ))}
          </ChartContainer>
        </Card>
      )}

      {/* ── Cycle history ── */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardIcon><History size={18} /></CardIcon>
            <CardTitle>{t('creditsDashboard.cycleHistory', { defaultValue: 'Histórico de Ciclos' })}</CardTitle>
          </CardHeader>

          <HistoryTable>
            <thead>
              <tr>
                <Th>{t('creditsDashboard.historyPeriod', { defaultValue: 'Período' })}</Th>
                <Th>{t('creditsDashboard.historyTier', { defaultValue: 'Plano' })}</Th>
                <Th $align="right">{t('creditsDashboard.historyBaseUsed', { defaultValue: 'Base usados' })}</Th>
                <Th $align="right">{t('creditsDashboard.historyExtraUsed', { defaultValue: 'Extras usados' })}</Th>
                <Th $align="right">{t('creditsDashboard.historyExpired', { defaultValue: 'Expirados' })}</Th>
                <Th $align="right">{t('creditsDashboard.historyPacks', { defaultValue: 'Pacotes' })}</Th>
              </tr>
            </thead>
            <tbody>
              {history.map((snap) => (
                <tr key={snap.period}>
                  <Td>{formatPeriod(snap.period)}</Td>
                  <Td>{snap.tier || '—'}</Td>
                  <Td $align="right">{(snap.baseCreditsUsed || 0).toLocaleString()}</Td>
                  <Td $align="right">{(snap.extraCreditsUsed || 0).toLocaleString()}</Td>
                  <Td $align="right">{(snap.creditsExpired || 0).toLocaleString()}</Td>
                  <Td $align="right">{snap.extraPacksPurchased || 0}</Td>
                </tr>
              ))}
            </tbody>
          </HistoryTable>
        </Card>
      )}
    </Wrap>
  );
};

/* ── Helpers ── */

/**
 * Build daily chart data from usageByAgent.
 * The usage data has per-agent category totals. We simulate daily distribution
 * across the current cycle days for the chart. Since the API returns aggregate
 * totals (not daily breakdowns), we distribute evenly across elapsed days.
 */
function buildDailyChartData(usageByAgent, agentMap) {
  const agentIds = Object.keys(usageByAgent);
  if (agentIds.length === 0) return { days: [], agentIds: [], maxTotal: 0 };

  const now = new Date();
  const cycleStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const elapsedDays = Math.max(1, Math.ceil((now - cycleStart) / (1000 * 60 * 60 * 24)));

  // Calculate total per agent
  const agentTotals = {};
  for (const [agentId, categories] of Object.entries(usageByAgent)) {
    agentTotals[agentId] = Object.values(categories).reduce((sum, v) => sum + v, 0);
  }

  // Build days array — distribute usage evenly with slight variation for visual interest
  const days = [];
  let maxTotal = 0;

  for (let d = 0; d < elapsedDays; d++) {
    const date = new Date(cycleStart);
    date.setDate(date.getDate() + d);
    const dayLabel = String(date.getDate()).padStart(2, '0');

    const dayAgents = {};
    let dayTotal = 0;

    for (const agentId of agentIds) {
      const total = agentTotals[agentId] || 0;
      // Distribute evenly across elapsed days
      const dailyAvg = Math.round(total / elapsedDays);
      dayAgents[agentId] = dailyAvg;
      dayTotal += dailyAvg;
    }

    if (dayTotal > maxTotal) maxTotal = dayTotal;
    days.push({ label: dayLabel, agents: dayAgents, total: dayTotal });
  }

  return { days, agentIds, maxTotal };
}

/**
 * Format period string (e.g. "2025-06") to a readable format.
 */
function formatPeriod(period) {
  if (!period) return '—';
  const [year, month] = period.split('-');
  const monthNames = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
  ];
  const idx = parseInt(month, 10) - 1;
  return `${monthNames[idx] || month}/${year}`;
}

/* ── Styled Components ── */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const Card = styled.div`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing[5]};
  box-shadow: ${theme.shadows.sm};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const CardIcon = styled.div`
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
`;

const BalanceValue = styled.span`
  font-size: ${theme.typography['2xl']};
  font-weight: ${theme.typography.bold};
  color: ${theme.colors.textPrimary};
`;

const BalanceTotal = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
`;

const Track = styled.div`
  height: 8px;
  background: ${theme.colors.gray100};
  border-radius: ${theme.radii.full};
  overflow: hidden;
  margin-bottom: ${theme.spacing[3]};
`;

const Fill = styled.div`
  height: 100%;
  border-radius: ${theme.radii.full};
  transition: width 0.4s ease;
`;

const BalanceMeta = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};

  strong {
    color: ${theme.colors.textSecondary};
  }
`;

const AlertBadge = styled.div`
  margin-top: ${theme.spacing[3]};
  font-size: ${theme.typography.xs};
  color: ${(p) => p.$color};
`;

/* ── Buy credits section ── */

const BuySection = styled.div`
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const BuyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.primary};
  color: ${theme.colors.textInverse};
  border: none;
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  align-self: flex-start;

  &:hover:not(:disabled) {
    background: ${theme.colors.primaryHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const PacksInfo = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const BuyResultMsg = styled.div`
  font-size: ${theme.typography.xs};
  color: ${(p) => (p.$success ? theme.colors.success : theme.colors.danger)};
`;

const BuyConfirmBox = styled.div`
  margin-top: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  border-radius: ${theme.radii.lg};
  border: 1.5px solid ${theme.colors.primary};
  background: ${theme.colors.primaryLight || '#f0f7ff'};
`;

const BuyConfirmTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
`;

const BuyConfirmDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
`;

const BuyConfirmRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  strong { color: ${theme.colors.textPrimary}; }
`;

const BuyConfirmActions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  justify-content: flex-end;
`;

const BuyConfirmCancel = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.border};
  background: white;
  font-size: ${theme.typography.xs};
  cursor: pointer;
  &:hover { background: ${theme.colors.gray100}; }
`;

const BuyConfirmBtn = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.radii.md};
  border: none;
  background: ${theme.colors.primary};
  color: white;
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

/* ── Agent list ── */

const AgentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const AgentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.bg};
  border-radius: ${theme.radii.md};
`;

const AgentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 0;
`;

const AgentIcon = styled.span`
  font-size: ${theme.typography.lg};
  flex-shrink: 0;
`;

const AgentName = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AgentCredits = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;
  flex-shrink: 0;
`;

const CostBadge = styled.span`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.primary};
  background: ${theme.colors.primaryLight};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.radii.full};
  white-space: nowrap;
  flex-shrink: 0;
`;

const EmptyText = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  margin: 0;
`;

/* ── Daily chart ── */

const ChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[3]};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: ${theme.radii.full};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textSecondary};
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 160px;
  overflow-x: auto;
  padding-bottom: ${theme.spacing[1]};
`;

const ChartColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 20px;
  max-width: 40px;
  height: 100%;
`;

const BarStack = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  flex: 1;
  align-items: stretch;
  justify-content: flex-start;
`;

const Bar = styled.div`
  width: 100%;
  border-radius: 2px 2px 0 0;
  min-height: 2px;
  transition: height 0.3s ease;
`;

const DayTotal = styled.span`
  font-size: 9px;
  color: ${theme.colors.textMuted};
  margin-top: 2px;
  height: 12px;
  line-height: 12px;
`;

const DayLabel = styled.span`
  font-size: 9px;
  color: ${theme.colors.textMuted};
  margin-top: 1px;
`;

/* ── History table ── */

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.sm};
`;

const Th = styled.th`
  text-align: ${(p) => p.$align || 'left'};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.xs};
  border-bottom: 1px solid ${theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  text-align: ${(p) => p.$align || 'left'};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  color: ${theme.colors.textSecondary};
  border-bottom: 1px solid ${theme.colors.gray100};
  white-space: nowrap;
`;

/* ── Loading / Error ── */

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[8]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.sm};

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.lg};
  color: ${theme.colors.danger};
  font-size: ${theme.typography.sm};
`;

export default CreditsDashboard;
